import { useState, useEffect, useCallback } from "react";
import { useUserData } from "lib/store/user";
import { useHomepageDatoProps } from "lib/store/homepage";
import {
  getZephrUserProfile,
  updateZephrUserProfile,
} from "lib/fetchers/zephr/customFields";
import {
  TOP_BANNER_VARIANTS,
  TOP_BANNER_BUTTON_TYPES,
  TOP_BANNER_DEFAULTS,
} from "lib/constants/topBanner";
import { datadogLogger } from "lib/datadog/logger";
import type { TopBannerVariant, TopBannerButtonType } from "../types";

interface TopBannerData {
  heading: string;
  subheading: string;
  primaryButtonLabel: string;
  primaryButtonUrl: string;
  secondaryButtonLabel: string;
  secondaryButtonUrl: string | null;
}

interface UseTopBannerReturn {
  variant: TopBannerVariant;
  bannerData: TopBannerData | null;
  isVisible: boolean;
  isLoading: boolean;
  shouldShow: boolean;
  handleDismiss: () => Promise<void>;
  trackButtonClick: (buttonType: TopBannerButtonType) => void;
}

/**
 * Private hook encapsulating top banner business logic.
 */
export const useTopBanner = (): UseTopBannerReturn => {
  const { isAuthorisedUser, isProUser, userId } = useUserData();
  const homepageData = useHomepageDatoProps();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const variant: TopBannerVariant =
    isAuthorisedUser && !isProUser
      ? TOP_BANNER_VARIANTS.PRO
      : TOP_BANNER_VARIANTS.SIGN_IN;

  const getBannerData = useCallback((): TopBannerData | null => {
    if (!homepageData) return null;

    if (variant === TOP_BANNER_VARIANTS.SIGN_IN) {
      return {
        heading: homepageData.topBannerSignInHeading ?? "",
        subheading: homepageData.topBannerSignInSubheading ?? "",
        primaryButtonLabel:
          homepageData.topBannerSignInPrimaryButtonLabel ?? "",
        primaryButtonUrl: homepageData.topBannerSignInPrimaryButtonUrl ?? "",
        secondaryButtonLabel:
          homepageData.topBannerSignInSecondaryButtonLabel ?? "",
        secondaryButtonUrl:
          homepageData.topBannerSignInSecondaryButtonUrl ?? "",
      };
    }

    return {
      heading: homepageData.topBannerProHeading ?? "",
      subheading: homepageData.topBannerProSubheading ?? "",
      primaryButtonLabel: homepageData.topBannerProPrimaryButtonLabel ?? "",
      primaryButtonUrl: homepageData.topBannerProPrimaryButtonUrl ?? "",
      secondaryButtonLabel: homepageData.topBannerProSecondaryButtonLabel ?? "",
      secondaryButtonUrl: homepageData.topBannerProSecondaryButtonUrl ?? "",
    };
  }, [homepageData, variant]);

  const bannerData = getBannerData();

  const saveDismissTimestamp = useCallback(async (): Promise<void> => {
    const timestamp = new Date().toISOString();

    if (variant === TOP_BANNER_VARIANTS.SIGN_IN) {
      if (typeof window === "undefined") return;

      try {
        localStorage.setItem(
          "sifted_top_banner_sign_in_dismissed_at",
          timestamp
        );
      } catch (error) {
        datadogLogger.error(
          "Failed to save sign-in banner dismiss to localStorage",
          undefined,
          error
        );
      }
    } else {
      if (!isAuthorisedUser) {
        datadogLogger.warn(
          "Cannot save pro banner dismiss - user not authorized",
          { variant }
        );
        return;
      }

      try {
        const result = await updateZephrUserProfile({
          top_banner_dismissed_at: timestamp,
        });

        if (!result.success) {
          throw new Error(
            "Failed to update Zephr profile with banner dismiss timestamp."
          );
        }
      } catch (error) {
        datadogLogger.error(
          "Failed to save pro banner dismiss to Zephr",
          { variant },
          error
        );
      }
    }
  }, [variant, isAuthorisedUser]);

  const checkShouldShowBanner = useCallback(async (): Promise<boolean> => {
    try {
      let dismissedAt: string | null = null;

      if (variant === TOP_BANNER_VARIANTS.SIGN_IN) {
        if (typeof window !== "undefined") {
          dismissedAt = localStorage.getItem(
            "sifted_top_banner_sign_in_dismissed_at"
          );
        }
      } else {
        const profile = await getZephrUserProfile();
        const attributeValue = profile?.top_banner_dismissed_at;
        dismissedAt =
          typeof attributeValue === "string" ? attributeValue : null;
      }

      if (!dismissedAt) {
        return true;
      }

      const dismissedDate = new Date(dismissedAt);
      if (isNaN(dismissedDate.getTime())) {
        return true;
      }

      const daysSinceDismiss = Math.floor(
        (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24)
      );

      const daysUntilRedisplay =
        variant === TOP_BANNER_VARIANTS.SIGN_IN
          ? homepageData?.topBannerSignInDaysUntilRedisplay ??
            TOP_BANNER_DEFAULTS.DAYS_UNTIL_REDISPLAY
          : homepageData?.topBannerProDaysUntilRedisplay ??
            TOP_BANNER_DEFAULTS.DAYS_UNTIL_REDISPLAY;

      return daysSinceDismiss >= daysUntilRedisplay;
    } catch (error) {
      datadogLogger.error(
        "Failed to check top banner dismiss status",
        { variant },
        error
      );
      return true;
    }
  }, [variant, homepageData]);

  const trackBannerView = useCallback(() => {
    if (typeof window !== "undefined" && (window as any).mixpanel) {
      (window as any).mixpanel.track("Top banner viewed", {
        variant,
        userId,
        heading: bannerData?.heading,
        timestamp: new Date().toISOString(),
      });
    }
  }, [variant, userId, bannerData]);

  const trackButtonClick = useCallback(
    (buttonType: TopBannerButtonType) => {
      if (
        typeof window !== "undefined" &&
        (window as any).mixpanel &&
        bannerData
      ) {
        const buttonLabel =
          buttonType === TOP_BANNER_BUTTON_TYPES.PRIMARY
            ? bannerData.primaryButtonLabel
            : bannerData.secondaryButtonLabel;
        const buttonUrl =
          buttonType === TOP_BANNER_BUTTON_TYPES.PRIMARY
            ? bannerData.primaryButtonUrl
            : bannerData.secondaryButtonUrl;

        (window as any).mixpanel.track(
          `Top banner ${buttonType} button clicked`,
          {
            variant,
            buttonLabel,
            buttonUrl,
            userId,
            timestamp: new Date().toISOString(),
          }
        );
      }
    },
    [variant, userId, bannerData]
  );

  const handleDismiss = useCallback(async (): Promise<void> => {
    await saveDismissTimestamp();

    if (typeof window !== "undefined" && (window as any).mixpanel) {
      (window as any).mixpanel.track("Top banner closed", {
        variant,
        userId,
        dismissedAt: new Date().toISOString(),
        timestamp: new Date().toISOString(),
      });
    }

    setIsVisible(false);
  }, [saveDismissTimestamp, variant, userId]);

  useEffect(() => {
    const initializeBanner = async () => {
      if (isProUser || !homepageData) {
        setIsLoading(false);
        return;
      }

      const shouldShow = await checkShouldShowBanner();

      if (shouldShow && bannerData) {
        setIsVisible(true);
        trackBannerView();
      }

      setIsLoading(false);
    };

    initializeBanner();
  }, [
    isProUser,
    homepageData,
    bannerData,
    checkShouldShowBanner,
    trackBannerView,
  ]);

  return {
    variant,
    bannerData,
    isVisible,
    isLoading,
    shouldShow:
      !isProUser &&
      isVisible &&
      !isLoading &&
      !!bannerData &&
      !!bannerData.heading &&
      !!bannerData.subheading &&
      !!bannerData.primaryButtonLabel &&
      !!bannerData.secondaryButtonLabel,
    handleDismiss,
    trackButtonClick,
  };
};
